/**
 * Token-baseret, diakritik- og bindestreg-insensitiv søgematcher.
 * Bruges af både autocomplete og resultatside, så søgeoplevelsen er konsistent.
 */

// Brand-aliases: normaliserede nøgleord -> ekstra tekst der appendes til haystack
// ved opslag. Fx skriver brugere ofte "vw" i stedet for "volkswagen".
const BRAND_ALIASES: Record<string, string[]> = {
  volkswagen: ['vw'],
  'mercedes-benz': ['mercedes', 'benz', 'mb'],
  'mercedes benz': ['mercedes', 'benz', 'mb'],
  bmw: ['beemer'],
  'land rover': ['landrover'],
  'rolls-royce': ['rollsroyce', 'rolls royce'],
  porsche: [],
  audi: [],
  skoda: ['škoda'],
  škoda: ['skoda'],
  citroen: ['citroën'],
  citroën: ['citroen'],
};

/**
 * Normaliserer en streng:
 * - lowercase
 * - fjerner diakritiske tegn (á→a, š→s, ø→o, ü→u, æ→ae)
 * - erstatter bindestreg, punktum, underscore og slash med mellemrum
 * - kollapser whitespace
 */
export function normalizeText(input: string): string {
  if (!input) return '';
  let s = input.toLowerCase();

  // Specialhåndter danske/nordiske bogstaver først (før NFD-decomposition)
  s = s
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/ß/g, 'ss');

  // Fjern alle øvrige diakritiske tegn
  s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Erstat separator-tegn med mellemrum
  s = s.replace(/[-_./\\]+/g, ' ');

  // Kollaps whitespace
  s = s.replace(/\s+/g, ' ').trim();

  return s;
}

/**
 * Bygger normaliseret haystack inkl. brand-aliases.
 */
function buildHaystack(parts: Array<string | null | undefined>): string {
  const normalizedParts = parts
    .filter((p): p is string => Boolean(p))
    .map(normalizeText);
  const joined = normalizedParts.join(' ');

  // Find aliases der matcher noget i haystack og tilføj dem
  const aliasAdditions: string[] = [];
  for (const [key, aliases] of Object.entries(BRAND_ALIASES)) {
    if (joined.includes(key)) {
      aliasAdditions.push(...aliases.map(normalizeText));
    }
  }

  return aliasAdditions.length > 0
    ? `${joined} ${aliasAdditions.join(' ')}`
    : joined;
}

/**
 * Returnerer true hvis ALLE tokens i query findes som substring i den
 * sammensatte haystack (brand + model + variant + aliases).
 *
 * Eksempler:
 *   tokenMatch(['Mercedes-Benz', 'CLA'], 'mercedes cla')  -> true
 *   tokenMatch(['Volkswagen', 'ID.4'], 'vw id4')          -> true
 *   tokenMatch(['Škoda', 'Enyaq'], 'skoda enyaq')         -> true
 *   tokenMatch(['Tesla', 'Model Y'], 'tesla y')           -> true
 */
export function tokenMatch(
  parts: Array<string | null | undefined>,
  query: string
): boolean {
  const q = normalizeText(query);
  if (!q) return true;
  const haystack = buildHaystack(parts);
  if (!haystack) return false;

  const tokens = q.split(' ').filter(Boolean);
  return tokens.every((t) => haystack.includes(t));
}

/**
 * Som tokenMatch, men returnerer en score (antal matchende tokens / totale tokens)
 * — bruges til "mente du"-forslag når 0 fuld-match findes.
 */
export function tokenScore(
  parts: Array<string | null | undefined>,
  query: string
): number {
  const q = normalizeText(query);
  if (!q) return 0;
  const haystack = buildHaystack(parts);
  if (!haystack) return 0;

  const tokens = q.split(' ').filter(Boolean);
  if (tokens.length === 0) return 0;
  const matched = tokens.filter((t) => haystack.includes(t)).length;
  return matched / tokens.length;
}
